import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  Box,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel as MuiFormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { PaginationParams, PinnedMorphotype, imageCountDefault } from 'redux/slices'
import { getSliceFromPagination } from 'utils/helpers'

import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import ActionPlotCellImages from '../cellSelectionDrawer/ActionPlotCellImages'
import CellImagePagination from '../cellSelectionDrawer/CellImagePagination'
import { usePlotData } from '../plot/usePlotData'
import { ChangeIconButton, DeleteIconButton, MergeIconButton } from '../shared'
import { calculatePercentageFromDataFrame } from '../utils/percentageCalculation'
import PercentageBreakdownTable from './PercentageBreakdownTable'

interface PinnedGroupContentProps {
  pinnedMorphotype: PinnedMorphotype
  onChange?(e: React.MouseEvent<HTMLElement>): void
  onDelete?(): void
  onMerge?(): void
  enableMerge?: boolean
}

export const PinnedGroupContent = ({
  pinnedMorphotype,
  onChange,
  onDelete,
  onMerge,
  enableMerge,
}: PinnedGroupContentProps): JSX.Element => {
  const {
    id: pinnedGroupId,
    name,
    active,
    cells: { points },
    pagination,
  } = pinnedMorphotype
  const selectedPointLength = points?.length ?? 0

  usePlotData()

  const { startIndex, endIndex } = getSliceFromPagination({
    count: selectedPointLength,
    page: pagination?.page ?? 0,
    itemsPerPage: pagination?.itemsPerPage ?? imageCountDefault,
  })

  const {
    dataFrame,
    setPinnedGroupActive,
    setPinnedGroupPagination,
    cellVisualizations: { exportCellMorphotype, cellInfoGroups },
    setPinnedGroupHighlighted,
    setPinnedGroupPercentageScope,
    hiddenCellInfoGroupValues,
    prefilteredCellIdSet,
  } = useCellVisualizationsSlice()

  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false)
  const [isCalculatingBreakdown, setIsCalculatingBreakdown] = useState(false)

  // Debounce breakdown type changes to avoid rapid recalculations
  const debounceTimeoutRef = useRef<NodeJS.Timeout>()

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  const handlePaginationChange = (newPagination: PaginationParams) => {
    setPinnedGroupPagination({ pinnedGroupId: pinnedMorphotype.id, pagination: newPagination })
  }

  const handleBreakdownTypeChange = useCallback(
    (breakdownType: 'RUN_ID' | 'SAMPLE_ID' | 'SAMPLE_TYPE') => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Show loading state immediately
      setIsCalculatingBreakdown(true)

      // Debounce the change by 100ms
      debounceTimeoutRef.current = setTimeout(() => {
        setPinnedGroupPercentageScope({
          pinnedGroupId: pinnedMorphotype.id,
          breakdownType,
        })
        // Clear loading state after a short delay to show the calculation happened
        setTimeout(() => setIsCalculatingBreakdown(false), 50)
      }, 100)
    },
    [pinnedMorphotype.id, setPinnedGroupPercentageScope]
  )

  // Calculate basic percentage (always the same regardless of breakdown type)
  const basicPercentageResult = useMemo(() => {
    if (!dataFrame || !points) {
      return {
        percentage: 0,
        selectedCount: 0,
        totalCount: 0,
        displayText: 'Percentage of cells: 0%',
        hasZeroCells: false,
      }
    }

    const selectedCellIds = points
      .map((p: { cellId?: string }) => p.cellId)
      .filter(Boolean) as string[]

    const selectedCount = selectedCellIds.length
    const totalCount = dataFrame.numRows()
    const percentage = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0
    const displayText = `Percentage of cells: ${percentage.toFixed(
      2
    )}% (${selectedCount.toLocaleString()} of ${totalCount.toLocaleString()})`

    return {
      percentage,
      selectedCount,
      totalCount,
      displayText,
      hasZeroCells: false,
    }
  }, [dataFrame, points])

  // Pre-calculate selected cell IDs to avoid recalculating on every render
  const selectedCellIds = useMemo(() => {
    if (!points) return []
    return points.map((p: { cellId?: string }) => p.cellId).filter(Boolean) as string[]
  }, [points])

  // Calculate breakdown only when needed and expanded
  const breakdownResult = useMemo(() => {
    if (!isBreakdownExpanded || !dataFrame || selectedCellIds.length === 0) {
      return null
    }

    const breakdownType = pinnedMorphotype.breakdownType || 'RUN_ID'

    // Ensure the breakdown type is set if it's not already
    if (!pinnedMorphotype.breakdownType) {
      setPinnedGroupPercentageScope({
        pinnedGroupId: pinnedMorphotype.id,
        breakdownType: 'RUN_ID',
      })
    }
    const result = calculatePercentageFromDataFrame(
      selectedCellIds,
      dataFrame,
      'entire_session',
      cellInfoGroups,
      hiddenCellInfoGroupValues,
      prefilteredCellIdSet,
      breakdownType
    )

    return result
  }, [
    isBreakdownExpanded,
    dataFrame,
    selectedCellIds,
    pinnedMorphotype.breakdownType,
    pinnedMorphotype.id,
    cellInfoGroups,
    hiddenCellInfoGroupValues,
    prefilteredCellIdSet,
    setPinnedGroupPercentageScope,
  ])

  const activeGroup = useMemo(() => {
    return exportCellMorphotype?.find((pinnedCell) => pinnedCell.groupId === pinnedGroupId)
  }, [exportCellMorphotype, pinnedGroupId])

  return (
    <CardContent
      key={`cc${pinnedGroupId}`}
      sx={{ my: '8px', py: '0' }}
      data-testid={`cell-group-${pinnedGroupId}`}
      onMouseOver={() =>
        setPinnedGroupHighlighted({
          pinnedGroupId,
          isHighlighted: true,
        })
      }
      onMouseOut={() =>
        setPinnedGroupHighlighted({
          pinnedGroupId,
          isHighlighted: false,
        })
      }
    >
      <Paper variant="outlined" sx={{ p: 1, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ alignItems: 'center' }}>
          <Stack key={`stack${pinnedGroupId}`} direction="row" justifyContent="space-between">
            <FormControlLabel
              checked={active}
              control={
                <Checkbox
                  data-testid="pinnedImagesCheckbox"
                  onChange={(e) =>
                    setPinnedGroupActive({
                      pinnedGroupId,
                      active: e.target.checked,
                    })
                  }
                />
              }
              label={name}
              data-testid="cell-group-name-label"
              sx={{
                ...(selectedPointLength === 0 && {
                  color: 'text.disabled',
                  '& .MuiCheckbox-root': { color: 'action.disabled' },
                }),
              }}
            />
            <ChangeIconButton onClick={onChange} data-testid="edit-cell-group-button" />
            <DeleteIconButton onClick={onDelete} data-testid="delete-cell-group-button" />
            <MergeIconButton
              iconButtonProps={{ disabled: !enableMerge }}
              data-testid="mergeButton"
              tooltip="Merge two or more selections"
              onClick={onMerge}
            />
          </Stack>
        </Stack>
        {activeGroup?.cellIds.length && (
          <Box my="8px">
            <Typography
              sx={{
                fontSize: '12px',
                lineHeight: '16px',
                textAlign: 'left',
              }}
            >
              {`${activeGroup?.cellIds.length}/10 selected for export`}
            </Typography>
          </Box>
        )}
        {pinnedMorphotype.active && (
          <>
            <ActionPlotCellImages
              pinnedGroupId={pinnedGroupId}
              cells={points?.slice(startIndex, endIndex)}
            />
            <Stack
              direction="row"
              data-testid={`${pinnedMorphotype.name}_pinnedText`}
              justifyContent="flex-end"
            >
              <CellImagePagination
                count={selectedPointLength}
                onPaginationChange={handlePaginationChange}
                defaultPage={pinnedMorphotype.pagination?.page}
                defaultItemsPerPage={pinnedMorphotype.pagination?.itemsPerPage}
              />
            </Stack>

            <hr />
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '11.7895px' }}>
                  {basicPercentageResult.displayText}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsBreakdownExpanded(!isBreakdownExpanded)}
                  sx={{ p: 0.5 }}
                >
                  {isBreakdownExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Stack>

              {isBreakdownExpanded && (
                <Stack spacing={1}>
                  {(() => {
                    if (isCalculatingBreakdown) {
                      return (
                        <Typography
                          sx={{ fontSize: '10px', color: 'text.secondary', fontStyle: 'italic' }}
                        >
                          Calculating breakdown...
                        </Typography>
                      )
                    }

                    if (breakdownResult?.breakdown && breakdownResult.breakdown.length > 0) {
                      return (
                        <PercentageBreakdownTable
                          breakdown={breakdownResult.breakdown}
                          filterFieldName={breakdownResult.filterFieldName}
                        />
                      )
                    }

                    return (
                      <Typography
                        sx={{ fontSize: '10px', color: 'text.secondary', fontStyle: 'italic' }}
                      >
                        No breakdown data available
                      </Typography>
                    )
                  })()}

                  <FormControl component="fieldset" size="small">
                    <FormLabel
                      component="legend"
                      sx={{ fontSize: '10px', color: 'text.secondary' }}
                    >
                      Detailed Breakdown
                    </FormLabel>
                    <RadioGroup
                      row
                      value={pinnedMorphotype.breakdownType || 'RUN_ID'}
                      onChange={(e) =>
                        handleBreakdownTypeChange(
                          e.target.value as 'RUN_ID' | 'SAMPLE_ID' | 'SAMPLE_TYPE'
                        )
                      }
                      sx={{ '& .MuiFormControlLabel-root': { marginRight: 2 } }}
                    >
                      <MuiFormControlLabel
                        value="RUN_ID"
                        control={<Radio size="small" />}
                        label="Run ID"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '10px' } }}
                      />
                      <MuiFormControlLabel
                        value="SAMPLE_ID"
                        control={<Radio size="small" />}
                        label="Sample ID"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '10px' } }}
                      />
                      <MuiFormControlLabel
                        value="SAMPLE_TYPE"
                        control={<Radio size="small" />}
                        label="Sample Type"
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: '10px' } }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Paper>
    </CardContent>
  )
}

export default PinnedGroupContent
